import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg,
    ArrowLeft
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { documentSchemaService, schemaAttributeService, documentAttributeService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import type { SchemaAttribute, CreateSchemaAttributeData, DocumentAttribute, ImportanceLevel } from '@/types/document-schema';
import { useTranslation } from 'react-i18next';

export const SchemaAttributesPage = () => {
    const { schemaId, documentType } = useParams<{ schemaId: string; documentType?: string }>();
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [schema, setSchema] = useState(documentSchemaService.getById(schemaId || ''));
    const [attributes, setAttributes] = useState<SchemaAttribute[]>([]);
    const [allAttributes, setAllAttributes] = useState<DocumentAttribute[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAttribute, setEditingAttribute] = useState<SchemaAttribute | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [attributeToDelete, setAttributeToDelete] = useState<string | null>(null);

    const [formData, setFormData] = useState<CreateSchemaAttributeData>({
        document_attribute_id: '',
        importance_level: 2,
        is_required: false,
        display_order: 0,
        custom_label: '',
        custom_help_text: '',
    });

    const importanceLevelOptions = [
        { id: '1', label: t('schemaAttributes.modal.fields.importanceLevels.error') },
        { id: '2', label: t('schemaAttributes.modal.fields.importanceLevels.warning') },
        { id: '3', label: t('schemaAttributes.modal.fields.importanceLevels.optional') },
    ];

    useEffect(() => {
        if (schemaId) {
            loadData();
        }
    }, [schemaId]);

    const loadData = () => {
        setIsLoading(true);
        if (schemaId) {
            const schemaData = documentSchemaService.getById(schemaId);
            setSchema(schemaData);
            const attrs = schemaAttributeService.getBySchemaId(schemaId);
            setAttributes(attrs);

            // Get attributes only for this document type (if documentType is in route)
            // Otherwise get all attributes (backward compatibility)
            if (documentType && schemaData) {
                const attrsForDocType = documentSchemaService.getAttributesByDocumentType(documentType as any);
                setAllAttributes(attrsForDocType);
            } else {
                const allAttrs = documentAttributeService.getAll();
                setAllAttributes(allAttrs);
            }
        }
        setIsLoading(false);
    };

    const filteredAttributes = attributes.filter((attr) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            attr.attribute?.attribute_key.toLowerCase().includes(term) ||
            attr.attribute?.label.toLowerCase().includes(term) ||
            attr.custom_label?.toLowerCase().includes(term)
        );
    });

    const handleOpenModal = (attr?: SchemaAttribute) => {
        if (attr) {
            setEditingAttribute(attr);
            setFormData({
                document_attribute_id: attr.document_attribute_id,
                importance_level: attr.importance_level,
                is_required: attr.is_required,
                display_order: attr.display_order,
                custom_label: attr.custom_label,
                custom_help_text: attr.custom_help_text,
            });
        } else {
            setEditingAttribute(null);
            setFormData({
                document_attribute_id: '',
                importance_level: 2,
                is_required: false,
                display_order: attributes.length,
                custom_label: '',
                custom_help_text: '',
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingAttribute(null);
    };

    const handleSave = () => {
        if (!schemaId) return;

        if (editingAttribute) {
            schemaAttributeService.update(editingAttribute.document_schema_attribute_id, {
                ...formData,
                document_schema_attribute_id: editingAttribute.document_schema_attribute_id,
            });
        } else {
            if (!formData.document_attribute_id) {
                alert(t('common.selectAttribute'));
                return;
            }
            schemaAttributeService.create(schemaId, formData);
        }
        loadData();
        handleCloseModal();
    };

    const handleDeleteClick = (id: string) => {
        setAttributeToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (attributeToDelete) {
            schemaAttributeService.delete(attributeToDelete);
            loadData();
            setIsDeleteModalOpen(false);
            setAttributeToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setAttributeToDelete(null);
    };

    const getImportanceBadge = (level: ImportanceLevel) => {
        switch (level) {
            case 1:
                return <Badge color="error" size="sm">{t('schemaAttributes.importance.error')}</Badge>;
            case 2:
                return <Badge color="warning" size="sm">{t('schemaAttributes.importance.warning')}</Badge>;
            case 3:
                return <Badge color="gray" size="sm">{t('schemaAttributes.importance.optional')}</Badge>;
        }
    };

    if (isLoading || !schema) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    const attributeOptions = allAttributes.map((attr) => ({
        id: attr.document_attribute_id,
        label: `${attr.label} (${attr.attribute_key})`,
    }));

    const currentDocType = schema ? DOCUMENT_TYPES.find((dt) => dt.key === schema.document_type) : null;
    const backUrl = documentType
        ? `/admin/documents/types/${documentType}/schemas`
        : '/admin/documents/schemas';

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            {documentType && (
                <div className="flex items-center gap-2 text-sm">
                    <Link
                        to="/admin/documents/types"
                        className="text-tertiary hover:text-primary"
                    >
                        {t('breadcrumb.documentTypes')}
                    </Link>
                    <span className="text-tertiary">/</span>
                    <Link
                        to={`/admin/documents/types/${documentType}/schemas`}
                        className="text-tertiary hover:text-primary"
                    >
                        {currentDocType?.label || documentType}
                    </Link>
                    <span className="text-tertiary">/</span>
                    <Link
                        to={backUrl}
                        className="text-tertiary hover:text-primary"
                    >
                        {t('breadcrumb.schemas')}
                    </Link>
                    <span className="text-tertiary">/</span>
                    <span className="text-primary">{schema.schema_name}</span>
                    <span className="text-tertiary">/</span>
                    <span className="text-primary">{t('breadcrumb.attributes')}</span>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    {!documentType && (
                        <Link
                            to={backUrl}
                            className="inline-flex items-center gap-2 text-sm text-tertiary hover:text-primary mb-4"
                        >
                            <ArrowLeft className="size-4" />
                            {t('schemaAttributes.backToSchemas')}
                        </Link>
                    )}
                    <h2 className="text-xl font-semibold text-primary">{t('schemaAttributes.title')}</h2>
                    <p className="text-tertiary mt-1">
                        {t('schemaAttributes.description')} - <span className="font-medium">{schema.schema_name}</span>
                    </p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('schemaAttributes.newAttribute')}
                </Button>
            </div>

            {/* Search */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <Input
                    placeholder={t('schemaAttributes.searchPlaceholder')}
                    value={searchTerm}
                    onChange={setSearchTerm}
                    icon={SearchLg}
                    size="md"
                />
            </div>

            {/* Attributes Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('schemaAttributes.title')} (${filteredAttributes.length})`}
                    description={t('schemaAttributes.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('schemaAttributes.headers.attributeKey')} isRowHeader />
                        <Table.Head label={t('schemaAttributes.headers.label')} />
                        <Table.Head label={t('schemaAttributes.headers.type')} />
                        <Table.Head label={t('schemaAttributes.headers.importance')} />
                        <Table.Head label={t('schemaAttributes.headers.required')} />
                        <Table.Head label={t('schemaAttributes.headers.enumValues')} />
                        <Table.Head label={t('schemaAttributes.headers.metadata')} />
                        <Table.Head label={t('schemaAttributes.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {filteredAttributes.map((attr) => (
                            <Table.Row key={attr.document_schema_attribute_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{attr.attribute?.attribute_key || '-'}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="font-medium text-primary">
                                        {attr.custom_label || attr.attribute?.label || '-'}
                                    </div>
                                    {attr.custom_help_text && (
                                        <div className="text-sm text-tertiary">{attr.custom_help_text}</div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color="gray" size="sm">{attr.attribute?.data_type || '-'}</Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    {getImportanceBadge(attr.importance_level)}
                                </Table.Cell>
                                <Table.Cell>
                                    {attr.is_required ? (
                                        <Badge color="error" size="sm">{t('common.yes')}</Badge>
                                    ) : (
                                        <span className="text-sm text-tertiary">{t('common.no')}</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {attr.attribute?.enum_values && attr.attribute.enum_values.length > 0 ? (
                                        <span className="text-sm text-tertiary">
                                            {attr.attribute.enum_values.join(', ')}
                                        </span>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {attr.attribute?.attribute_metadata ? (
                                        <span className="text-sm text-tertiary font-mono">
                                            {JSON.stringify(attr.attribute.attribute_metadata).substring(0, 30)}...
                                        </span>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleOpenModal(attr)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('schemaAttributes.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteClick(attr.document_schema_attribute_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('schemaAttributes.actions.remove')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {filteredAttributes.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingAttribute ? t('schemaAttributes.modal.edit') : t('schemaAttributes.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    <Select
                                        label={t('schemaAttributes.modal.fields.attribute')}
                                        items={attributeOptions}
                                        selectedKey={formData.document_attribute_id}
                                        onSelectionChange={(key) => setFormData({ ...formData, document_attribute_id: key as string })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>

                                    <Select
                                        label={t('schemaAttributes.modal.fields.importanceLevel')}
                                        items={importanceLevelOptions}
                                        selectedKey={String(formData.importance_level)}
                                        onSelectionChange={(key) => setFormData({ ...formData, importance_level: Number(key) as ImportanceLevel })}
                                        isRequired
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id}>
                                                {item.label}
                                            </Select.Item>
                                        )}
                                    </Select>

                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_required || false}
                                            onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                                            className="rounded border-secondary"
                                        />
                                        <label className="text-sm text-primary">{t('schemaAttributes.modal.fields.isRequired')}</label>
                                    </div>

                                    <Input
                                        label={t('schemaAttributes.modal.fields.displayOrder')}
                                        value={String(formData.display_order || 0)}
                                        onChange={(value) => setFormData({ ...formData, display_order: Number(value) || 0 })}
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemaAttributes.modal.fields.customLabel')}
                                        placeholder={t('schemaAttributes.modal.fields.customLabelPlaceholder')}
                                        value={formData.custom_label || ''}
                                        onChange={(value) => setFormData({ ...formData, custom_label: value })}
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemaAttributes.modal.fields.customHelpText')}
                                        placeholder={t('schemaAttributes.modal.fields.customHelpTextPlaceholder')}
                                        value={formData.custom_help_text || ''}
                                        onChange={(value) => setFormData({ ...formData, custom_help_text: value })}
                                        size="md"
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseModal} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleSave} size="md" color="primary">
                                        {editingAttribute ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('modal.confirmDelete')}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('modal.confirmDeleteMessage')}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button onClick={handleDeleteCancel} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleDeleteConfirm} size="md" color="primary-destructive">
                                        {t('modal.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
